import { Broker, CodeDeployment, DeploymentStatus } from '@amzn/amazonmq-opsconsole-client';
import {
    KeyValuePairs,
    Link,
    SpaceBetween,
    StatusIndicator
} from '@amzn/awsui-components-react';
import React, { useMemo } from 'react';
import useConfig from '../../../../hooks/useConfig';
import { RebootEvent } from '../../../../types';
import { createIsengardLink } from '../../../../utils';

enum BrokerState {
    RESTARTING = 'RESTARTING',
    FAILED = 'FAILED'
}

enum StatusType {
    ERROR = 'error',
    SUCCESS = 'success',
    IN_PROGRESS = 'in-progress',
    WARNING = 'warning'
}

interface Props {
    broker: Broker;
    asgName: string;
    asgArn: string;
    latestDeployment?: CodeDeployment;
    latestWorkflowStep?: RebootEvent;
}

const AutoScalingGroupLink: React.FC<{
    asgName: string;
    asgArn: string;
    cellAccountId?: string;
    isengardEndpoint?: string;
    region?: string;
}> = ({ asgName, asgArn, cellAccountId, isengardEndpoint, region }) => {
    const isengardLink = useMemo(() => {
        if (!isengardEndpoint || !asgArn || !cellAccountId || !region) return '';
        return createIsengardLink(
            isengardEndpoint,
            cellAccountId,
            'ReadOnly',
            `ec2/v2/home?region=${region}#AutoScalingGroupDetails:groupName=${asgName}`
        );
    }, [isengardEndpoint, asgArn, cellAccountId, region, asgName]);

    if (!asgArn) return <>-</>;

    return (
        <Link external href={isengardLink}>
            {asgName}
        </Link>
    );
};

const DeploymentStatusIndicator: React.FC<{ deployment: CodeDeployment }> = ({ deployment }) => {
    const statusType = useMemo(() => {
        if (!deployment) return StatusType.WARNING;

        switch (deployment.status) {
            case DeploymentStatus.Failed:
                return StatusType.ERROR;
            case DeploymentStatus.Succeeded:
            case DeploymentStatus.Ready:
                return StatusType.SUCCESS;
            case DeploymentStatus.InProgress:
            case DeploymentStatus.Queued:
            case DeploymentStatus.Baking:
                return StatusType.IN_PROGRESS;
            default:
                return StatusType.WARNING;
        }
    }, [deployment]);

    return (
        <SpaceBetween direction="horizontal" size="xs">
            <span>{deployment.deploymentId}</span>
            <StatusIndicator type={statusType}>
                {deployment.status}
            </StatusIndicator>
        </SpaceBetween>
    );
};

const RebootStatusIndicator: React.FC<{ brokerState: string }> = ({ brokerState }) => {
    if (!brokerState) {
        return <StatusIndicator type={StatusType.WARNING}>Unknown Status</StatusIndicator>;
    }

    if (brokerState.includes(BrokerState.FAILED)) {
        return <StatusIndicator type={StatusType.ERROR}>Restart Failed</StatusIndicator>;
    } else if (brokerState === BrokerState.RESTARTING) {
        return <StatusIndicator type={StatusType.IN_PROGRESS}>Restarting</StatusIndicator>;
    } else {
        return <StatusIndicator type={StatusType.SUCCESS}>No Reboot in Progress</StatusIndicator>;
    }
};

const WorkflowStepDisplay: React.FC<{ event?: RebootEvent }> = ({ event }) => {
    const stepName = useMemo(() => {
        if (!event) return 'Unknown';
        if (!event.details) return event.type || 'Unknown';

        try {
            const parsedDetails = JSON.parse(event.details);
            return parsedDetails?.name || event.type || 'Unknown';
        } catch (error) {
            console.error(error);
        }
    }, [event]);

    if (!event) return <>-</>;

    return (
        <SpaceBetween direction="horizontal" size="xs">
            <span>{stepName}</span>
        </SpaceBetween>
    );
};

const SummaryHeader: React.FC<Props> = ({
    broker,
    asgName,
    asgArn,
    latestDeployment,
    latestWorkflowStep
}) => {
    const config = useConfig();
    const brokerState = broker?.summary?.brokerState || '';
    const cellAccountId = broker?.brokerInfo?.cellAccountId;

    if (!asgName) return null;

    return (
        <>
            <SpaceBetween direction="vertical" size="xs">
                <KeyValuePairs
                    columns={4}
                    items={[
                        {
                            label: 'Auto Scaling Group',
                            value: (
                                <AutoScalingGroupLink
                                    asgName={asgName}
                                    asgArn={asgArn}
                                    cellAccountId={cellAccountId}
                                    isengardEndpoint={config?.isengardEndpoint}
                                    region={config?.region}
                                />
                            )
                        },
                        {
                            label: 'Current Status',
                            value: <RebootStatusIndicator brokerState={brokerState} />
                        },
                        {
                            label: 'Latest Deployment',
                            value: latestDeployment ?
                                <DeploymentStatusIndicator deployment={latestDeployment} /> :
                                '-'
                        },
                        {
                            label: 'Latest Workflow Step',
                            value: <WorkflowStepDisplay event={latestWorkflowStep} />
                        },
                    ]}
                />
            </SpaceBetween>
        </>
    );
};

export default React.memo(SummaryHeader);